import type { RequestInfo } from '@warp-drive/core/types/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';
import type { Store } from '@warp-drive/core';
import type { TasActor } from '../utils/tas-actor.ts';
import type { TasAppHistory } from '../types/tuition-assistance';
import type { TasProgramInstance } from '../data/resources/tas-program-instance.type.ts';

const tasProgramInstancesUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'tas-program-instances' },
    op: 'query',
    resourcePath: 'tas-program-instances',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

/** GET /tas-program-instances/:id/history */
export function getTasProgramInstanceHistoryRequest(id: string): RequestInfo {
  return {
    url: tasProgramInstancesUrl(`${id}/history`),
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Fetches transition history and assigns it to the program instance. The explicit store supports
 * owner-free resource callers.
 */
export async function loadTasProgramInstanceHistory(
  store: Store,
  instance: TasProgramInstance
): Promise<TasAppHistory> {
  const response = await store.request(getTasProgramInstanceHistoryRequest(instance.id as string));
  const history = (response.content as { data: TasAppHistory }).data;
  instance.history = history;
  return history;
}

/**
 * State-transition events accepted by `/tas-program-instances/:id/transition/state/:event`.
 * Named explicitly so callers (notably program-approval-footer's
 * dynamic dispatch) can be typed against the closed set.
 */
export type TasProgramInstanceTransitionEvent =
  | 'request-program-approval'
  | 'approve-program'
  | 'reject-program'
  | 'decline-program'
  | 'request-completion-approval'
  | 'approve-completion'
  | 'reject-completion'
  | 'decline-completion'
  | 'request-withdrawal-approval'
  | 'approve-withdrawal'
  | 'reject-withdrawal'
  | 'decline-withdrawal'
  | 'abandon';

/**
 * Generic `triggerActionAndReload` body the legacy adapter built:
 *   PATCH /tas-program-instances/:id/transition/state/:event
 *   { data: { note, by: <actor> } }
 *
 * Callers are responsible for invoking `instance.reload()` after the
 * request resolves — the legacy adapter folded that into the same
 * helper; we keep the builder shape pure.
 */
export function transitionTasProgramInstanceRequest(
  id: string,
  event: TasProgramInstanceTransitionEvent,
  actor: TasActor,
  note = ''
): RequestInfo {
  // Top-level body shape; the legacy adapter sent these fields flat
  // via `this.ajax(url, 'PATCH', { data: { note, by } })` (Ember Data's
  // `data` option IS the body, not a JSON:API envelope).
  return {
    url: tasProgramInstancesUrl(`${id}/transition/state/${event}`),
    method: 'PATCH',
    body: JSON.stringify({ note, by: actor }),
    cacheOptions: { [SkipCache]: true },
  };
}

// Named convenience wrappers — match the legacy adapter method names so
// callers grep cleanly. Behavior identical to passing the event string
// to `transitionTasProgramInstanceRequest`.
const wrap =
  (event: TasProgramInstanceTransitionEvent) =>
  (id: string, actor: TasActor, note = ''): RequestInfo =>
    transitionTasProgramInstanceRequest(id, event, actor, note);

export const requestProgramApprovalRequest = wrap('request-program-approval');
/** Legacy adapter named this `requestChanges`; the URL event is `decline-program`. */
export const requestChangesRequest = wrap('decline-program');
export const requestProgramCompletionChangesRequest = wrap('decline-completion');
export const requestProgramWithdrawalChangesRequest = wrap('decline-withdrawal');
export const abandonProgramRequest = wrap('abandon');

/** PATCH /tas-program-instances/:id/transition/state/rescind — rescind the last approver action. */
export function rescindLastApproverActionRequest(id: string): RequestInfo {
  return {
    url: tasProgramInstancesUrl(`${id}/transition/state/rescind`),
    method: 'PATCH',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * PATCH /tas-program-instances/:id/insert-reviewer
 * Inserts a new reviewer at the given ordinal in the approval chain.
 */
export function insertReviewerRequest(
  id: string,
  employeeId: number,
  ordinal: number
): RequestInfo {
  // Top-level body shape — legacy adapter sent `{ 'employee-id', ordinal }`
  // flat, not inside a JSON:API `data` envelope.
  return {
    url: tasProgramInstancesUrl(`${id}/insert-reviewer`),
    method: 'PATCH',
    body: JSON.stringify({ 'employee-id': employeeId, ordinal }),
    cacheOptions: { [SkipCache]: true },
  };
}
